import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, Calendar, Loader2, Receipt,
    Clock, Car, Users, FileText, AlertTriangle, Shield, FileArchive, Download, X, EuroIcon, EyeOff, Building2
} from 'lucide-react';
import { AmministrazioneService, SettingsService, SedeService } from '../services/apiService';
import { formatOreInHM, formatRole } from '../utils/utils';
import { useAuth } from '../contexts/AuthContext';
import { UserRole, Sede } from '../types';

import { generateReportPdf, generateReportPdfBlob, generateReportSenzaCosti, generateReportSenzaCostiBlob } from '../services/generateReportPdf';
import type { ReportPdfOptions } from '../services/generateReportPdf';
import JSZip from "jszip";

// Converte un URL immagine in data URL PNG: jsPDF.addImage non fa fetch da solo.
const toBase64Png = async (url: string | null | undefined): Promise<string | null> => {
    if (!url) return null;
    try {
        const res = await fetch(url, { mode: 'cors' });
        const blob = await res.blob();
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width;
        canvas.height = bmp.height;
        canvas.getContext('2d')!.drawImage(bmp, 0, 0);
        return canvas.toDataURL('image/png');
    } catch (e) {
        console.warn('[ReportMensile] conversione logo fallita:', e);
        return null;
    }
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({
                     label, value, unit, icon: Icon,
                     iconColor, iconBg, borderColor, valueColor
                 }: {
    label: string; value: number | string; unit: string;
    icon: React.ElementType; iconColor: string; iconBg: string;
    borderColor: string; valueColor: string;
}) => (
    <div
        className="relative group overflow-hidden rounded-2xl p-6 bg-white transition-shadow duration-300 hover:shadow-lg"
        style={{ border: `1.5px solid ${borderColor}` }}
    >
        <div
            className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"
            style={{ background: iconColor }}
        />
        <div
            className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
            style={{ background: iconBg }}
        >
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-400 mb-1">{label}</p>
        <div className="flex items-end gap-1.5">
            <span className="text-3xl font-black leading-none" style={{ color: valueColor }}>{value}</span>
            <span className="text-sm font-semibold text-slate-400 pb-0.5">{unit}</span>
        </div>
    </div>
);

// ─── Main View ────────────────────────────────────────────────────────────────

export const ReportMensileView: React.FC = () => {
    const navigate = useNavigate();
    const { user, currentSedeId, sedi: sediGestite } = useAuth();
    const isAdmin = user?.role === UserRole.ADMIN;
    // Sedi gestite dal responsabile: può filtrare tra queste o vederle tutte;
    // il backend rifiuta le sedi che non gestisce.
    const sediResponsabile: { id: number; name: string }[] = user?.role === UserRole.RESPONSABILE
        ? sediGestite.map(s => ({ id: s.id, name: s.name }))
        : [];
    const isResponsabileMultiSede =
        user?.role === UserRole.RESPONSABILE && sediResponsabile.length > 1;
    // Il picker serve all'ADMIN e al responsabile con più sedi; con una sola sede non si sceglie nulla.
    const showSedePicker = isAdmin || isResponsabileMultiSede;

    const [selectedMonth, setSelectedMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    // ADMIN parte dalla sede selezionata nell'app; il responsabile multi-sede da 'ALL' (tutte le sue).
    const [sedis, setSedis] = useState<Sede[]>([]);
    const [selectedSede, setSelectedSede] = useState<number | 'ALL'>(
        isResponsabileMultiSede ? 'ALL'
            : typeof currentSedeId === 'number' ? currentSedeId : 'ALL'
    );

    // ADMIN vede tutte le sedi caricate, il responsabile solo quelle che gestisce.
    const sedeOptions: { id: number; name: string }[] = isAdmin
        ? sedis.map(s => ({ id: s.id, name: s.name }))
        : sediResponsabile;
    const allOptionLabel = isAdmin ? 'Tutte le sedi' : 'Tutte le mie sedi';

    const [loading, setLoading] = useState(false);
    const [reportList, setReportList] = useState<any[]>([]);

    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const res = await SedeService.getAll();
                if (res.success) setSedis(res.data);
            } catch (e) {
                console.warn('[ReportMensile] sedi non caricabili:', e);
            }
        })();
    }, [isAdmin]);

    // Logo e ragione sociale già convertiti per jsPDF.addImage.
    const [pdfOpts, setPdfOpts] = useState<ReportPdfOptions>({});

    useEffect(() => {
        (async () => {
            try {
                const res: any = await SettingsService.getAll();
                const settings = res?.data?.[0] || res?.[0] || null;
                if (!settings) return;
                const logoDataUrl = await toBase64Png(settings.logoUrl);
                setPdfOpts({
                    logoDataUrl,
                    agencyName: settings.agencyName || settings.ragioneSociale || undefined,
                });
            } catch (e) {
                console.warn('[ReportMensile] settings non caricabili:', e);
            }
        })();
    }, []);

    // Modale scelta importi
    type DownloadIntent = { type: 'zip' | 'completo' | 'single'; stat?: any };
    const [downloadIntent, setDownloadIntent] = useState<DownloadIntent | null>(null);
    const [yearStr, monthStr] = selectedMonth.split('-');
    const anno = parseInt(yearStr);
    const mese = parseInt(monthStr);

    const handleExportZip = async (conImporti: boolean) => {
        const zip = new JSZip();
        for (const stat of reportList) {
            const blob = conImporti
                ? generateReportPdfBlob([stat], anno, mese, pdfOpts)
                : generateReportSenzaCostiBlob([stat], anno, mese, pdfOpts);
            const prefix = conImporti ? 'Report' : 'Riepilogo';
            const fileName = `${prefix}_${stat.nomeCompleto.replace(/\s+/g, '_')}_${selectedMonth}.pdf`;
            zip.file(fileName, blob);
        }
        const content = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${conImporti ? 'Report' : 'Riepilogo'}_Mensile_${selectedMonth}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
    };

    useEffect(() => {
        const fetchDati = async () => {
            if (!selectedMonth) return;
            setLoading(true);
            try {
                const [yyyy, mm] = selectedMonth.split('-');
                console.log(`Richiesta report per: Anno ${yyyy}, Mese ${mm}, Sede ${selectedSede}`);

                const res = await AmministrazioneService.getReportMensile(parseInt(yyyy), parseInt(mm), selectedSede);

                console.log("Risposta API Completa:", res);

                if (res.success && res.data) {
                    setReportList(res.data);
                } else {
                    console.warn("L'API ha risposto con success:false o dati nulli");
                    setReportList([]);
                }
            } catch (error) {
                console.error("Errore AJAX:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDati();
    }, [selectedMonth, selectedSede]);

    const totali = useMemo(() => reportList.reduce((acc, curr) => ({
        oreInv: acc.oreInv + curr.oreInvestigazioni,
        oreSic: acc.oreSic + curr.oreSicurezza,
        km:     acc.km     + curr.kmPercorsi,
        euro:   acc.euro   + curr.granTotaleEuro
    }), { oreInv: 0, oreSic: 0, km: 0, euro: 0 }), [reportList]);

    const handleExportCompleto = (conImporti: boolean) => {
        if (conImporti) generateReportPdf(reportList, anno, mese, pdfOpts);
        else            generateReportSenzaCosti(reportList, anno, mese, pdfOpts);
    };

    const handleExportSingle = (stat: any, conImporti: boolean) => {
        if (conImporti) generateReportPdf([stat], anno, mese, pdfOpts);
        else            generateReportSenzaCosti([stat], anno, mese, pdfOpts);
    };

    const handleConfirmDownload = async (conImporti: boolean) => {
        if (!downloadIntent) return;
        setDownloadIntent(null);
        if (downloadIntent.type === 'zip') await handleExportZip(conImporti);
        else if (downloadIntent.type === 'completo') handleExportCompleto(conImporti);
        else if (downloadIntent.type === 'single') handleExportSingle(downloadIntent.stat, conImporti);
    };

    const meseLabel = new Date(anno, mese - 1, 1)
        .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

    return (
        <div className="min-h-screen p-4 lg:p-6 xl:p-10" style={{ background: '#f5f7fa' }}>
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
                    <div>
                        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors mb-4">
                            <ChevronLeft size={14}/> Torna Indietro
                        </button>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md" style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' }}>
                                <FileText size={22} className="text-white"/>
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 leading-tight">Ore &amp; Spese Collaboratori</h1>
                                <p className="text-xs font-medium text-slate-400 mt-0.5 capitalize">Prospetto economico · {meseLabel}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Sede picker: ADMIN (tutte le sedi) o responsabile multi-sede (le sue) */}
                        {showSedePicker && (
                            <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-200">
                                <Building2 size={16} className="text-blue-500"/>
                                <select
                                    value={selectedSede}
                                    onChange={e => setSelectedSede(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                                    className="text-sm font-bold text-slate-800 outline-none bg-transparent cursor-pointer pr-1"
                                >
                                    <option value="ALL">{allOptionLabel}</option>
                                    {sedeOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Month picker */}
                        <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-200">
                            <Calendar size={16} className="text-blue-500"/>
                            <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="text-sm font-bold text-slate-800 outline-none bg-transparent cursor-pointer" />
                        </div>

                        {/* Pulsante ZIP */}
                        <button
                            onClick={() => setDownloadIntent({ type: 'zip' })}
                            disabled={loading || reportList.length === 0}
                            className="flex items-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-xl shadow-md transition-all active:scale-95"
                            title="Scarica tutti i PDF separati in un archivio ZIP"
                        >
                            <FileArchive size={18} />
                            <span className="text-xs font-bold uppercase tracking-widest">Scarica ZIP</span>
                        </button>

                        {/* Pulsante PDF Completo */}
                        <button
                            onClick={() => setDownloadIntent({ type: 'completo' })}
                            disabled={loading || reportList.length === 0}
                            className="flex items-center gap-2 px-4 py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white rounded-xl shadow-md transition-all active:scale-95"
                        >
                            <FileText size={18} />
                            <span className="text-xs font-bold uppercase tracking-widest">Report Completo</span>
                        </button>
                    </div>
                </div>

                {/* KPI */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {/*
                      Le somme aggregate sono arrotondate a step di 10 minuti solo per la
                      visualizzazione; i record giornalieri restano esatti per il calcolo dei costi.
                    */}
                    <KpiCard
                        label="Ore Investigazioni" value={formatOreInHM(totali.oreInv, true)} unit="" icon={Clock}
                        iconColor="#3b82f6" iconBg="#eff6ff" borderColor="#bfdbfe" valueColor="#1d4ed8"
                    />
                    <KpiCard
                        label="Ore Sicurezza" value={formatOreInHM(totali.oreSic, true)} unit="" icon={Shield}
                        iconColor="#7c3aed" iconBg="#f5f3ff" borderColor="#ddd6fe" valueColor="#6d28d9"
                    />
                    <KpiCard
                        label="Km Percorsi" value={totali.km.toLocaleString('it-IT')} unit="km" icon={Car}
                        iconColor="#059669" iconBg="#ecfdf5" borderColor="#a7f3d0" valueColor="#047857"
                    />
                    {/* Euro card */}
                    <div className="relative group overflow-hidden rounded-2xl p-6 bg-white shadow-sm hover:shadow-lg transition-shadow duration-300"
                         style={{ border: '1.5px solid #fde68a' }}>
                        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-amber-400 opacity-10 group-hover:opacity-20 transition-opacity duration-300"/>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-amber-50">
                            <Receipt size={20} className="text-amber-500"/>
                        </div>
                        <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-amber-500/80 mb-1">Da Liquidare</p>
                        <div className="flex items-end gap-1">
                            <span className="text-base font-bold text-amber-400 pb-0.5">€</span>
                            <span className="text-3xl font-black leading-none text-amber-600">
                                {totali.euro.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tabella */}
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">

                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60">
                        <div className="flex items-center gap-2.5">
                            <Users size={16} className="text-blue-500"/>
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                                Spettanze per Operatore
                            </span>
                        </div>
                        {reportList.length > 0 && (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                                {reportList.length} operatori
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className="p-16 flex flex-col items-center gap-3">
                            <Loader2 className="animate-spin text-blue-500" size={28}/>
                            <span className="text-xs text-slate-400 font-medium">Caricamento dati...</span>
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-2 px-2 lg:mx-0 lg:px-0">
                            <table className="w-full min-w-[1000px] text-sm">
                                <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="py-3.5 pl-6 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400">Collaboratore</th>
                                    <th className="py-3.5 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">Tariffe</th>
                                    <th className="py-3.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-400">Compenso Ore</th>
                                    <th className="py-3.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-400">Rimb. KM</th>
                                    <th className="py-3.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-400">Spese</th>
                                    <th className="py-3.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-400">Totale</th>
                                    <th className="py-3.5 pr-6 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">Azioni</th>
                                </tr>
                                </thead>
                                <tbody>
                                {reportList.map((stat, idx) => (
                                    <tr key={stat.utenteId} className="group hover:bg-slate-50/70 transition-colors">
                                        <td className="py-4 pl-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-xs font-black text-blue-600 bg-blue-50 border border-blue-100">
                                                    {stat.nomeCompleto?.split(' ').map((w: string) => w[0]).slice(0, 2).join('')}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-800">{stat.nomeCompleto}</div>
                                                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{formatRole(stat.ruolo)}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 text-center font-mono text-[10px] text-slate-400">
                                            {stat.tariffaOrariaInv}€/h · {stat.tariffaOrariaSic}€/h
                                        </td>
                                        <td className="py-4 text-right">
                                            <div className="font-bold text-slate-800">€ {(stat.totaleEuroInvestigazioni + stat.totaleEuroSicurezza).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                            {/* Totale ore arrotondato a 10 minuti solo per la visualizzazione. */}
                                            <div className="text-[10px] text-slate-400">{formatOreInHM(stat.oreInvestigazioni + stat.oreSicurezza, true)}</div>
                                        </td>
                                        <td className="py-4 text-right font-bold text-slate-800">€ {stat.totaleEuroKm.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                        <td className="py-4 text-right font-bold text-amber-600">€ {stat.totaleEuroSpeseExtra.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                        <td className="py-4 text-right">
                                            <span className="inline-block px-3 py-1.5 rounded-lg text-sm font-black text-emerald-700 bg-emerald-50 border border-emerald-100">
                                                € {stat.granTotaleEuro.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </span>
                                        </td>
                                        {/* Azioni singolo collaboratore */}
                                        <td className="py-4 pr-6 text-center">
                                            <button
                                                onClick={() => setDownloadIntent({ type: 'single', stat })}
                                                className="p-2.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm group-hover:shadow-md"
                                                title={`Scarica PDF per ${stat.nomeCompleto}`}
                                            >
                                                <Download size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>

                                {/* Footer totali */}
                                {reportList.length > 1 && (
                                    <tfoot>
                                    <tr className="bg-slate-50 border-t border-slate-200">
                                        <td className="py-4 pl-6 text-xs font-black uppercase tracking-widest text-slate-500" colSpan={2}>
                                            Totale Mensile
                                        </td>
                                        <td className="py-4 text-right font-black text-slate-800 text-sm">
                                            € {reportList.reduce((a, c) => a + (c.totaleEuroInvestigazioni + c.totaleEuroSicurezza), 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </td>
                                        <td className="py-4 text-right font-black text-slate-800 text-sm">
                                            € {reportList.reduce((a, c) => a + c.totaleEuroKm, 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </td>
                                        <td className="py-4 text-right font-black text-amber-600 text-sm">
                                            € {reportList.reduce((a, c) => a + c.totaleEuroSpeseExtra, 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </td>
                                        <td className="py-4 pr-6 text-right">
                                            <span className="inline-block px-3 py-1.5 rounded-lg font-black text-sm text-emerald-700 bg-emerald-100 border border-emerald-200">
                                                € {totali.euro.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </span>
                                        </td>
                                    </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </div>

                {/* Modale scelta importi */}
                {downloadIntent && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-slate-50">
                                <h2 className="text-base font-bold text-slate-900">Includi importi economici?</h2>
                                <button onClick={() => setDownloadIntent(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-full">
                                    <X size={18}/>
                                </button>
                            </div>
                            <div className="p-6 space-y-3">
                                <p className="text-sm text-slate-500">Scegli se il PDF deve includere tariffe orarie, rimborsi km e importi da liquidare, oppure solo i dati operativi (ore e km).</p>
                                <button
                                    onClick={() => handleConfirmDownload(true)}
                                    className="w-full flex items-center gap-4 p-4 border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition-all text-left group"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0 group-hover:bg-blue-500 transition-colors">
                                        <EuroIcon size={18} className="text-blue-600 group-hover:text-white transition-colors"/>
                                    </div>
                                    <div>
                                        <div className="font-bold text-slate-800 text-sm">Sì, includi importi</div>
                                        <div className="text-xs text-slate-400">Tariffe orarie, rimborsi km, totali da liquidare</div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => handleConfirmDownload(false)}
                                    className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-xl transition-all text-left group"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-slate-600 transition-colors">
                                        <EyeOff size={18} className="text-slate-500 group-hover:text-white transition-colors"/>
                                    </div>
                                    <div>
                                        <div className="font-bold text-slate-800 text-sm">No, solo attività</div>
                                        <div className="text-xs text-slate-400">Ore, km, orari — nessun dato economico</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Disclaimer */}
                <div className="mt-5 flex items-start gap-3 rounded-xl px-5 py-4 bg-blue-50 border border-blue-100">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5 text-blue-400"/>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        I calcoli si basano <strong className="text-blue-700 font-semibold">esclusivamente</strong> sui
                        report e le spese <strong className="text-blue-700 font-semibold">Approvate</strong>, con le
                        tariffe <strong className="text-blue-700 font-semibold">attuali</strong> dei collaboratori: modificando
                        una tariffa vengono ricalcolati anche i mesi precedenti. Scarica il PDF dei mesi già liquidati
                        prima di cambiare le tariffe.
                    </p>
                </div>

            </div>
        </div>
    );
};

export default ReportMensileView;